

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { NotificationListData, NotificationResponse } from '@/lib/shared-types/api/endpoints/notifications';
import { getResponseData, isSuccessResponse } from '@/lib/shared-types/api/response';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { toast } from 'sonner';
import { useDurableObjectChannel } from '@/hooks/use-durable-object-channel';
import { useAuthStore } from '@/lib/stores/auth';
import { getNotificationNavigation } from '@/lib/utils/navigation-helpers';
import { handleNotificationNavigation } from '@/lib/utils/notification-auth';

export function NotificationBell() {
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState<NotificationResponse[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const { user } = useAuthStore();

    const fetchLeNotifications = async () => {
        if (api.isBackendUnavailable()) {
            return;
        }

        // In production use SWR or React Query
        try {
            const response = await api.get<NotificationListData>('notifications?limit=5');
            if (!response.data || !isSuccessResponse(response)) {
                console.error('Failed to fetch notifications:', response.error?.message);
                return;
            }
            const data = getResponseData(response)!;
            if (data.notifications) {
                setNotifications(data.notifications);
                // Count unread (assuming backend doesn't give count directly, but it should)
                // Currently backend list returns array.
                // Ideally we check "read" status.
                const unread = data.notifications.filter((n: NotificationResponse) => !n.read).length;
                setUnreadCount(unread);
            }
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        fetchLeNotifications();
    }, []);

    // Real-time WebSocket subscription for new notifications
    const handleNotificationMessage = useCallback((message: any) => {
        if (message.type === 'new_notification' && message.notification) {
            // Add new notification to the list
            setNotifications(prev => [message.notification, ...prev]);
            setUnreadCount(prev => prev + 1);
            
            // Show toast for new notification
            toast.info(message.notification.title || 'New notification', {
                description: message.notification.message,
                duration: 5000,
            });
        }
    }, []);

    useDurableObjectChannel({
        channel: user?.id ? `notifications:${user.id}` : '',
        enabled: !!user?.id,
        onMessage: handleNotificationMessage,
    });

    const markRead = async (id: number) => {
        try {
            await api.put(`api/notifications/${id}/read`);
            // Update local state
            setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (e) {
            toast.error('Failed to mark read');
        }
    };

    const handleNotificationClick = async (notification: NotificationResponse) => {
        // Navigate based on type and data
        const targetUrl = getNotificationNavigation(notification);
        if (!targetUrl) {
            return;
        }

        if (!api.isBackendUnavailable()) {
            await markRead(notification.id);
        }

        await handleNotificationNavigation(targetUrl, (safeUrl) => {
            navigate(safeUrl);
        });
    };

    const handleViewAll = () => {
        navigate('/notifications');
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative">
                    <Bell className="h-5 w-5" />
                    {unreadCount > 0 && (
                        <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-red-600 animate-pulse border border-background" />
                    )}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
                <DropdownMenuLabel>Notifications</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <div className="max-h-[300px] overflow-y-auto">
                    {notifications.length === 0 ? (
                        <div className="p-4 text-center text-sm text-muted-foreground">
                            No notifications
                        </div>
                    ) : (
                        notifications.map(n => (
                            <DropdownMenuItem
                                key={n.id}
                                className={`flex flex-col items-start gap-1 p-3 cursor-pointer ${!n.read ? 'bg-muted/50 font-medium' : ''}`}
                                onClick={() => handleNotificationClick(n)}
                            >
                                <div className="flex justify-between w-full">
                                    <span className="text-sm font-semibold">{n.title}</span>
                                    <span className="text-xs text-muted-foreground">{new Date(n.createdAt).toLocaleDateString()}</span>
                                </div>
                                <p className="text-xs text-muted-foreground line-clamp-2">{n.message}</p>
                            </DropdownMenuItem>
                        ))
                    )}
                </div>
                {notifications.length > 0 && (
                    <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                            className="w-full justify-center text-xs text-primary cursor-pointer"
                            onClick={handleViewAll}
                        >
                            View All
                        </DropdownMenuItem>
                    </>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
